import type { AssetAnimation } from '@/api/types.gen';
import {
  DISPLAY_ANIMATION_MAX_MS,
  displayAnimationWarning,
  displaySizeWarning,
} from '@/lib/media-validation/display';

/**
 * The warnings beside a Display banner: it loops forever or animates past The
 * Trade Desk's 15 seconds, or (given `byteSize`) it is over TTD's recommended
 * 150 KB. Advice only: SAM never refuses an animation (owner,
 * 2026-10-08), so this renders nothing for a still banner or one that stops in
 * time, and never blocks anything.
 *
 * `exceeds` is the server's own verdict where it sends one (the review page's
 * `exceeds_animation_limit`); it shows the warning even if the timings did not
 * come with it.
 */
export function DisplayAnimationNote({
  animation,
  exceeds = false,
  byteSize,
}: {
  animation?: Pick<AssetAnimation, 'frame_count' | 'cycle_ms' | 'plays'> | null;
  exceeds?: boolean;
  byteSize?: number | bigint | null;
}) {
  const warning =
    displayAnimationWarning(animation) ??
    (exceeds
      ? `Animates for longer than ${DISPLAY_ANIMATION_MAX_MS / 1000} s. The Trade Desk asks for ${DISPLAY_ANIMATION_MAX_MS / 1000} s at most, then a still frame; some publishers may refuse it.`
      : null);
  const sizeWarning = displaySizeWarning(byteSize);
  if (!warning && !sizeWarning) return null;
  return (
    <>
      {warning && (
        <div className="asset-warn" role="note" data-display-animation-warning>
          <span style={{ flexShrink: 0 }} aria-hidden="true">
            ⚠
          </span>
          <span>{warning}</span>
        </div>
      )}
      {sizeWarning && (
        <div className="asset-warn" role="note" data-display-size-warning>
          <span style={{ flexShrink: 0 }} aria-hidden="true">
            ⚠
          </span>
          <span>{sizeWarning}</span>
        </div>
      )}
    </>
  );
}
